import React, {
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import PropTypes from 'prop-types';
import useTrackingId from '../hooks/useTrackingId.js';

RIQPillInput.propTypes = {
    className: PropTypes.string,
    placeholder: PropTypes.string,
    pills: PropTypes.array,
    setPills: PropTypes.func,
    inputUniqueId: PropTypes.string.isRequired,
    multiline: PropTypes.bool
};

const fontCssProperties = ['font-style', 'font-variant', 'font-weight', 'font-size', 'font-family'];
function getFont(el) {
    const font = _.map(fontCssProperties, (prop) => {
        return window.getComputedStyle(el, null).getPropertyValue(prop);
    }).join(' ');

    return font;
}

function measureText(str, font = null) {
    let canvas = measureText._canvas;
    if (!canvas) {
        // NOTE memoize the canvas element so we only ever create one
        canvas = measureText._canvas = document.createElement('canvas');
    }

    const context = canvas.getContext('2d');
    context.font = font;
    const width = context.measureText(str).width;
    return width;
}

function measureInput(input, font = getFont(input)) {
    const width = measureText(input.value, font);
    return width;
}

function Pill({disabled, handleDelete, handleInputBlur, handleInputChange, handleEditPill, handleInputEnter, index, operator, pillInputId, position, value}) {

    const inputRef = useRef();

    useLayoutEffect(() => {
        if (!disabled) {
            const input = inputRef.current;
            input.focus();

            const font = getFont(input);
            const minWidth = measureText(' ', font);
            const inputWidth = measureInput(input, font);
            const width = Math.max(inputWidth, minWidth);
            input.style.width = `${width}px`;
        }

        const el = document.getElementById(`inputScrolling-${pillInputId}`);
        if (el) {
            el.scrollLeft = el.scrollWidth;
        }
    });

    const [inputValue, setInputValue] = useState(value);

    function onClick(e) {
        handleEditPill(index);
        e.stopPropagation();
    }

    function onDelete(e) {
        handleDelete(index);
        e.stopPropagation();
    }

    function onChange(e) {
        const newValue = e.target.value;
        setInputValue(newValue);
        handleInputChange(newValue);
    }

    let pillClassName = 'tlw-flex tlw-items-center';
    // NOTE this feature can be turned off if `operator` is falsy, but when
    // enabled it will insert a separating operator label as content BEFORE the
    // pill when it is not the first one in the list
    if (operator && position > 0) {
        pillClassName += ' before_tlw-block before_tlw-text-xs before_tlw-pr-3 before_tlw-content-[attr(data-operator)] before_tlw-text-gray-650 ';
    }

    return (
        <div
            className={pillClassName}
            data-operator={operator}>
            <div
                key={`pill-${index}`}
                onClick={handleEditPill && onClick}
                // Calling preventDefault on the mouse down will allow the click event to run before onBlur is called on the input
                onMouseDown={handleEditPill && ((e) => e.preventDefault())}
                aria-hidden='true'
                className={`tlw-mr-2 tlw-flex tlw-max-w-[300px] tlw-items-center tlw-rounded-[15px] ${disabled ? 'tlw-bg-gray-150' : 'tlw-bg-gray-500'} tlw-py-1 tlw-pl-5 tlw-pr-2.5 ${disabled ? 'tlw-text-black' : 'tlw-text-white'}`}
            >
                {disabled ?
                    <span
                        aria-hidden='true'
                        className='tlw-w-full tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap'
                    >
                        { value }
                    </span> :
                    <input
                        className='tlw-border-none tlw-bg-transparent tlw-p-0 tlw-outline-none'
                        ref={inputRef}
                        onKeyDown={(e) => handleInputEnter(e, inputValue, setInputValue)}
                        onChange={onChange}
                        onBlur={() => handleInputBlur(index, inputValue, setInputValue)}
                        value={inputValue}
                    />
                }
                <div className='tlw-cursor-pointer tlw-pl-2'>
                    <span
                        className={`tlw-flex tlw-h-[2rem] tlw-w-[2rem] tlw-items-center tlw-justify-center tlw-rounded-[50%] tlw-p-1 ${disabled ? 'tlw-text-black hover_tlw-bg-gray-500 hover_tlw-text-white' : 'tlw-text-white hover_tlw-bg-gray-150 hover_tlw-text-black'}`}
                        onClick={onDelete}
                        // Calling preventDefault on the mouse down will allow the click event to run before onBlur is called on the input
                        onMouseDown={(e) => e.preventDefault()}
                        aria-hidden='true'
                    >
                        ×
                    </span>
                </div>
            </div>
        </div>
    );
}

const NEW_PILL_IDX = 'NEW_PILL';
export default function RIQPillInput({className, focusedPillIdx, initialFocusState = false, inputUniqueId, multiline, operator = 'OR', placeHolder, pills, setPills, trackingId}) {

    const [focus, setFocus] = useState(initialFocusState);
    const [editPillIdx, setEditPillIdx] = useState(-1);
    const [editPillValue, setEditPillValue] = useState(null);

    useLayoutEffect(() => {
        if (_.isFinite(focusedPillIdx) && focusedPillIdx >= 0) {
            setEditPillIdx(focusedPillIdx);
            setEditPillValue(null);
            setFocus(false);
        }
    }, [focusedPillIdx]);

    // NOTE this method works in concert with `focusedPillIdx`, powered
    // upstream by validation, to determine if a pill can be added. to do so it
    // examines the list of pills, and when in an error state (indicated by the
    // presence of a `focusedPillIdx`) checks to see if there are duplicates
    // (the only current error condition we consider at this time). notice that
    // only when the error pill has been changed (i.e., `editPillValue` is not
    // null) that we will replace the value in `pills` with it (thereby
    // allowing a user to edit the pill by removing or adding characters,
    // making it unique, elligible for add)
    const canAddTerms = useMemo(() => {
        const occurrences = {};

        const terms = [].concat(pills);
        if (_.isFinite(focusedPillIdx) && editPillValue !== null) {
            terms[focusedPillIdx] = editPillValue;
        }

        return _.every(terms, (term) => {
            const normalizedTerm = term.toLocaleLowerCase();
            if (!occurrences[normalizedTerm]) {
                return (occurrences[normalizedTerm] = true);
            }
            return false;
        });
    }, [editPillValue, focusedPillIdx, pills]); // eslint-disable-line react-hooks/exhaustive-deps

    // use this function for keyboard events
    function handleInputEnter(e, inputValue, setInputValue) {
        if (canAddTerms && inputValue !== '' && e.key === 'Enter') {
            if (editPillIdx === -1) {
                setPills([...pills, inputValue]);
                setInputValue('');
            } else {
                const updatedPills = _.clone(pills);
                updatedPills[editPillIdx] = inputValue;
                setPills(updatedPills);
                setEditPillIdx(-1);
            }
            setEditPillValue(null);
        }
    }

    function handleInputChange(newValue) {
        setEditPillValue(newValue);
    }

    function handleInputBlur(indexBeingBlurred, inputValue, setInputValue) {
        if (!canAddTerms) {
            return;
        }

        if (editPillIdx === -1) {
            if (inputValue !== '') {
                setPills(currentPills => {
                    if (_.isArray(currentPills)) {
                        return _.flatten([...currentPills, inputValue]);
                    }

                    return [inputValue];
                });
                setInputValue('');
            }

        } else {
            const updatedPills = _.clone(pills);
            updatedPills[editPillIdx] = inputValue;
            setPills(updatedPills);
        }
        setFocus(false);
        setEditPillIdx(-1);
        setEditPillValue(null);
    }

    function handleEditPill(index) {
        if (!canAddTerms) {
            return;
        }

        if (editPillValue && editPillIdx !== index) {
            // if user was editing a pill then clicked another pill, we want to make sure to commit the changes they had on that first pill.
            if (editPillIdx !== -1) {
                pills[editPillIdx] = editPillValue;
                setPills([...pills]);
            } else {
                setPills(currentPills => {
                    if (_.isArray(currentPills)) {
                        return _.flatten([...currentPills, editPillValue]);
                    }

                    return [editPillValue];
                });
            }
        }

        setEditPillIdx(index);
        setEditPillValue(null);
    }

    function handleDelete(index) {
        setEditPillIdx(-1);

        const updatedPills = _.reject(pills, (pill, idx) => {
            return idx === index;
        });

        setPills(updatedPills);

        if (index === NEW_PILL_IDX) {
            setFocus(false);
        }
    }

    function handleInput() {
        setFocus(true);
    }

    let wrapperClassName = 'tlw-flex tlw-items-center tlw-px-2 tlw-text-md tlw-rounded-sm tlw-border tlw-border-solid tlw-bg-white tlw-text-gray-625 empty_tlw-pl-7.5';
    if (!multiline) {
        wrapperClassName += ' tlw-overflow-hidden tlw-overscroll-auto tlw-scroll-smooth';
    }

    if (className) {
        wrapperClassName += ` ${className}`;
    }

    if (focus) {
        wrapperClassName += ' tlw-border-blue-400 tlw-shadow-inner tlw-outline-none';
    } else {
        wrapperClassName += ' tlw-border-gray-600 tlw-outline-none';
    }

    const trackingRef = useTrackingId(trackingId);

    return (
        <>
            {focus === false && pills.length === 0 && <input
                className={`${wrapperClassName} tlw-py-3 tlw-placeholder-gray-625`}
                placeholder={placeHolder}
                onFocus={handleInput}
                ref={trackingRef}
            />}
            {(focus === true || pills.length >= 1) && <div
                id={`inputScrolling-${inputUniqueId}`}
                className={wrapperClassName}
                tabIndex='-1'
                onFocus={() => handleInput()}
                ref={trackingRef}
            >
                <div className={`tlw-m-2 tlw-mr-0 tlw-flex ${multiline ? 'tlw-flex-wrap' : ''} tlw-gap-y-2`}>
                    {pills.map((pill, index) => (
                        (index !== editPillIdx) ?
                            <Pill
                                key={`${pill}-${index}`}
                                index={index}
                                operator={operator}
                                position={index}
                                value={pill}
                                disabled={true}
                                handleDelete={handleDelete}
                                handleEditPill={handleEditPill}
                                pillInputId={inputUniqueId}
                            /> :
                            <Pill
                                key={`${pill}-${index}`}
                                index={index}
                                operator={operator}
                                position={index}
                                value={pill}
                                disabled={false}
                                handleInputEnter={handleInputEnter}
                                handleInputChange={handleInputChange}
                                handleInputBlur={handleInputBlur}
                                handleDelete={handleDelete}
                                pillInputId={inputUniqueId}
                            />
                    ))}
                    {focus === true && editPillIdx === -1 &&
                        <Pill
                            key={pills.length}
                            index={NEW_PILL_IDX}
                            operator={operator}
                            position={_.size(pills)}
                            value=''
                            disabled={false}
                            handleInputEnter={handleInputEnter}
                            handleInputChange={handleInputChange}
                            handleInputBlur={handleInputBlur}
                            handleDelete={handleDelete}
                            pillInputId={inputUniqueId}
                        />
                    }
                </div>

            </div>}
        </>
    );
}
